import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { musicApi, type ArtistLookupResult } from "@/api/music";
import type { Artist } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field, SearchInput, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useMemo } from "react";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

export function ArtistList() {
  const [adding, setAdding] = useState(false);
  const [filter, setFilter] = useState("");
  const { data: artists, isLoading } = useQuery({
    queryKey: ["music", "artists"],
    queryFn: musicApi.listArtists,
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!artists) return [];
    if (!lower) return artists;
    return artists.filter((a) => a.name.toLowerCase().includes(lower));
  }, [artists, lower]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Music"
        title="Artists"
        description="Musicians you're monitoring. Tap a card for the full discography."
        actions={<Button onClick={() => setAdding(true)}>Add artist</Button>}
      />

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter artists"
          />
        </div>
        {artists && (
          <span className="font-mono text-xs text-foreground/55">
            {filter
              ? `${filtered.length} of ${artists.length}`
              : `${artists.length} artists`}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton.Cards count={10} cols={5} />
      ) : !artists || artists.length === 0 ? (
        <EmptyState
          glyph="cassette"
          title="No artists yet"
          description="Search MusicBrainz to add an artist's full discography, or use Discover to expand from a seed."
          action={
            <div className="flex items-center gap-2">
              <Button onClick={() => setAdding(true)}>Add manually</Button>
              <Link to="/music/discover">
                <Button variant="secondary">Try Discover</Button>
              </Link>
            </div>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        // Backend already returns rows ordered by sort_name asc — keep that
        // order rather than re-sorting on the client.
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {filtered.map((a) => (
            <ArtistCard key={a.id} artist={a} />
          ))}
        </div>
      )}

      <AddArtistModal open={adding} onClose={() => setAdding(false)} />
    </div>
  );
}

function ArtistCard({ artist }: { artist: Artist }) {
  return (
    <Link
      to="/music/artists/$id"
      params={{ id: String(artist.id) }}
      className="group relative overflow-hidden rounded-md border border-line bg-surface/30 transition hover:border-line-strong"
    >
      <div className="relative">
        <PosterTile
          kind="artists"
          id={artist.id}
          url={artist.poster_url}
          name={artist.sort_name || artist.name}
          ratio="2/3"
          className="transition group-hover:scale-[1.02]"
        />
        {!artist.monitored && (
          <div className="absolute right-2 top-2">
            <Status variant="chip" tone="neutral">
              off
            </Status>
          </div>
        )}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background via-background/80 to-transparent"
        />
      </div>
      <div className="p-3">
        <div className="line-clamp-1 font-display text-sm font-medium leading-tight tracking-display-tight">
          {artist.name}
        </div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
          artist
        </div>
      </div>
    </Link>
  );
}

/**
 * Reusable poster tile with a three-step fallback chain:
 *   1. `/api/v1/covers/{kind}/{id}.jpg` — locally cached cover
 *   2. `url` — upstream CDN URL (TMDB / TheAudioDB / Cover Art Archive)
 *   3. Letter tile
 *
 * Step 1 is only attempted when both `kind` and `id` are passed. The
 * cached endpoint returns 404 when we haven't fetched the cover yet,
 * which trips the next step automatically — no extra plumbing.
 */
export type CoverKind = "artists" | "albums" | "movies" | "series";

export function PosterTile({
  kind,
  id,
  url,
  name,
  ratio,
  className,
}: {
  kind?: CoverKind;
  id?: number;
  url: string | null;
  name: string;
  ratio: "2/3" | "1/1";
  className?: string;
}) {
  const aspect = ratio === "2/3" ? "aspect-[2/3]" : "aspect-square";
  const [cacheFailed, setCacheFailed] = useState(false);
  const [upstreamFailed, setUpstreamFailed] = useState(false);

  const cacheUrl =
    kind != null && id != null ? `/api/v1/covers/${kind}/${id}.jpg` : null;

  if (cacheUrl && !cacheFailed) {
    return (
      <img
        src={cacheUrl}
        alt=""
        className={cn(aspect, "w-full object-cover", className)}
        loading="lazy"
        onError={() => setCacheFailed(true)}
      />
    );
  }
  if (url && !upstreamFailed) {
    return (
      <img
        src={url}
        alt=""
        className={cn(aspect, "w-full object-cover", className)}
        loading="lazy"
        onError={() => setUpstreamFailed(true)}
      />
    );
  }
  const letter = (name.trim()[0] ?? "?").toUpperCase();
  return (
    <div
      className={cn(
        aspect,
        "grid w-full place-items-center bg-surface-2/60",
        className,
      )}
    >
      <span className="select-none text-2xl font-semibold text-foreground/40">
        {letter}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add artist modal — search MusicBrainz, then POST /api/v1/artists.
// ---------------------------------------------------------------------------

function AddArtistModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ArtistLookupResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [addingMbid, setAddingMbid] = useState<string | null>(null);

  const reset = () => {
    setQ("");
    setResults(null);
    setSearching(false);
    setAddingMbid(null);
  };

  const closeAndReset = () => {
    onClose();
    window.setTimeout(reset, 200);
  };

  const search = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearching(true);
    try {
      const hits = await musicApi.lookupArtists(trimmed);
      setResults(hits.slice(0, 10));
    } catch (err) {
      toast(err instanceof Error ? err.message : "Lookup failed", "error");
    } finally {
      setSearching(false);
    }
  };

  const addMut = useMutation({
    mutationFn: (mbid: string) =>
      musicApi.addArtist({ mbid, monitored: true }),
    onSuccess: (res) => {
      toast(`Added ${res.artist.name}`, "success");
      qc.invalidateQueries({ queryKey: ["music", "artists"] });
      closeAndReset();
    },
    onError: (err) => {
      toast(err instanceof Error ? err.message : "Add failed", "error");
      setAddingMbid(null);
    },
  });

  return (
    <Modal open={open} onClose={closeAndReset} title="Add artist" width="lg">
      <div className="space-y-4">
        <p className="rounded border border-line bg-surface/40 px-3 py-2 text-xs text-foreground/70">
          This will add the artist's full discography. To add only specific
          albums, use{" "}
          <Link to="/music/discover" className="underline hover:text-accent">
            Discover
          </Link>{" "}
          instead.
        </p>

        <form onSubmit={search}>
          <Field label="Search MusicBrainz">
            <div className="flex gap-2">
              <TextInput
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Billy Strings"
              />
              <Button type="submit" loading={searching}>
                Search
              </Button>
            </div>
          </Field>
        </form>

        {results !== null && results.length > 0 && (
          <div className="max-h-96 space-y-2 overflow-y-auto pr-2">
            {results.map((r) => (
              <div
                key={r.mbid}
                className="flex items-start justify-between gap-3 rounded border border-line bg-surface/40 p-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{r.name}</div>
                  {r.disambiguation && (
                    <div className="text-xs text-foreground/60">
                      {r.disambiguation}
                    </div>
                  )}
                  <div className="mt-1 truncate font-mono text-[10px] text-foreground/40">
                    {r.mbid}
                  </div>
                </div>
                <Button
                  size="sm"
                  loading={addingMbid === r.mbid}
                  onClick={() => {
                    setAddingMbid(r.mbid);
                    addMut.mutate(r.mbid);
                  }}
                >
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}

        {results !== null && results.length === 0 && !searching && (
          <p className="text-sm text-foreground/60">
            No results — try a different query.
          </p>
        )}
      </div>
    </Modal>
  );
}
